<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title>
            <data-search-bar
                :title="title"
                :help-href="helpUrl"
                :options="dataSearchBarOptions"
                :is-filters-active="isFiltersActive"
                show-filters
                @change="onDataSearchBarChange"
                @filter="onFilterClick"
                @refresh="onRefreshClick"
            >
                <template v-slot:second-column>
                    <v-col cols="auto" class="px-5">
                        <v-sheet elevation="2" class="chart-slot pa-2">
                            <space-status-chart
                                @click="onChartClick"
                            ></space-status-chart>
                        </v-sheet>
                    </v-col>
                </template>
                <template v-slot:below-title>
                    <v-col>
                        <filter-chips
                            :items="filterChips"
                            @change="onFilterChipsChange"
                        ></filter-chips>
                    </v-col>
                </template>
                <template v-if="tab > 0" v-slot:pagination>
                    <div
                        class="text-caption font-weight-medium text-center py-4"
                        style="min-height: 58px;"
                    >
                        <span>{{ responseQuery.totalElements }} rows</span>
                    </div>
                </template>
                <template v-slot:disclaimer>
                    <div class="pt-1 text-body-2 font-italic">
                        Last Update {{ lastHealthPollDate }} - See Device detail
                        pages for current status
                    </div>
                </template>
            </data-search-bar>
        </v-card-title>
        <v-card-text class="scrollable-content py-2">
            <v-sheet elevation="5" class="fill-height scrollable-container">
                <v-tabs
                    v-model="tab"
                    grow
                    color="accent"
                    class="thin-tabs mb-2 px-2"
                >
                    <v-tab>
                        <v-icon class="mx-2">{{ icons.viewList }}</v-icon>
                        <span>List view</span>
                    </v-tab>
                    <v-tab>
                        <v-icon class="mx-2">{{ icons.building }}</v-icon>
                        <span>Topology view</span>
                    </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab" class="px-2 scrollable-content">
                    <v-tab-item>
                        <data-table-view
                            v-model="selectedItems"
                            :headers="tableFields"
                            :items="spaces"
                            :sort-by="sortBy"
                            :sort-desc="sortDesc"
                            :page="page"
                            :items-per-page="itemsPerPage"
                            :loading="isDataLoading"
                            :zebra-stripping="zebraStripping"
                            :computed-fields="computedFields"
                            :show-select="true"
                            :toolbar-actions="toolbarActions"
                            allow-export
                            @update-sort-by="onUpdateSortBy"
                            @export="onExportDataClick"
                            @toolbar="onToolbarClick"
                        >
                            <!-- ============== Table actions template ============== -->
                            <template v-slot:tableButtons="{ item }">
                                <span
                                    v-show="
                                        $can('read', 'Space') &&
                                            $can(
                                                'show',
                                                'Buttons/spaces/viewSpace'
                                            )
                                    "
                                    class="px-3"
                                >
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon
                                                color="accent"
                                                cy-data="view-row"
                                                @click="
                                                    onViewSpaceClick(
                                                        item,
                                                        $event
                                                    )
                                                "
                                                v-on="on"
                                                >{{ icons.viewItem }}
                                            </v-icon>
                                        </template>
                                        <span class="text-caption"
                                            >View space details</span
                                        >
                                    </v-tooltip>
                                </span>
                            </template>
                        </data-table-view>
                    </v-tab-item>
                    <v-tab-item>
                        <div class="pt-2 pb-4 px-2">
                            <topology-view
                                :items="topologyData"
                            ></topology-view>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-sheet>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="
                            $can('create', 'Space') &&
                                $can('show', 'Buttons/spaces/addSpace')
                        "
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Add new space"
                        cy-data="add-row"
                        v-on="on"
                        @click="onAddSpaceClick"
                    >
                        <v-icon>{{ icons.add }}</v-icon>
                    </v-btn>
                </template>
                <span>Add new space</span>
            </v-tooltip>
            <component
                :is="spaceDetailsComponent"
                :show-dialog="showSpaceDetailsDialog"
                :space-id="selectedSpaceId"
                @close-dialog="onCloseSpaceDetailsDialog"
                @upload="onUploadClick"
            >
            </component>
            <component
                :is="exportDialogComponent"
                title="Export to CSV: Spaces"
                :entity-name="exportEntityName"
                :data-source-params="exportParams"
                :output-fields="exportFields"
                :output-file-name="exportFileName"
                @close="onExportDialogClose"
            ></component>
        </v-card-text>
        <space-filters
            v-model="showFilters"
            title="Space filters"
            :filters="filters"
            @apply="onApplyFilters"
        ></space-filters>
        <add-remove-tags ref="tagsDialog"></add-remove-tags>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        <lineup-selection-dialog
            ref="lineupSelection"
        ></lineup-selection-dialog>
        <space-upload-dialog
            :show="showUploadDialog"
            @close="onUploadDialogClose"
        ></space-upload-dialog>
    </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { isEmpty, isEmptyValues } from '@/common/helpers';
import DataSearchBar from '@/components/Data/DataSearchBar';
import DataTableView from '@/components/Data/DataTableView';
import CsvDataExporter from '@/components/Export/CsvDataExporter';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import FilterChips from '@/components/Filters/FilterChips';
import SpaceDetailsDialog from './components/SpaceDetailsDialog';
import TopologyView from '../topology/TopologyView';
import SpaceFilters from './filters/SpaceFilters';
import AddRemoveTags from '../tags/AddRemoveTags';
import LineupSelectionDialog from '../venues/components/LineupSelectionDialog';
import SpaceUploadDialog from './components/SpaceUploadDialog';
import topologyUtil from '@/store/utils/topology';
import SpaceStatusChart from './components/SpaceStatusChart';

export default {
    name: 'SpacesPage',
    components: {
        'data-search-bar': DataSearchBar,
        'data-table-view': DataTableView,
        'space-details-dialog': SpaceDetailsDialog,
        'csv-data-exporter': CsvDataExporter,
        'space-filters': SpaceFilters,
        'add-remove-tags': AddRemoveTags,
        'confirmation-dialog': ConfirmationDialog,
        'lineup-selection-dialog': LineupSelectionDialog,
        'topology-view': TopologyView,
        'filter-chips': FilterChips,
        'space-upload-dialog': SpaceUploadDialog,
        'space-status-chart': SpaceStatusChart
    },
    data() {
        return {
            title: 'Spaces',
            isDataLoading: false,
            selectedSpaceId: '',
            spaceDetailsComponent: '',
            showSpaceDetailsDialog: false,
            showUploadDialog: false,
            exportDialogComponent: '',
            exportEntityName: 'spaces',
            exportParams: {},
            tab: null,
            filteredTopology: null,
            filterChips: [],
            showFilters: false,
            lastFocusedElement: null,
            selectedItems: [],
            lastHealthPollDate: ''
        };
    },
    computed: {
        ...mapState('spaces', [
            'tableFields',
            'computedFields',
            'filterFields',
            'exportFields',
            'exportFileName',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping',
            'allSpaces',
            'helpUrl'
        ]),
        ...mapState('spaces', {
            spaces: 'items'
        }),
        dataSearchBarOptions() {
            return {
                page: this.requestQuery.number,
                itemsPerPage: this.requestQuery.size,
                pageCount: this.responseQuery.totalPages,
                itemsLength: this.responseQuery.totalElements,
                query: this.requestQuery.q
            };
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        filters() {
            return this.filterQuery;
        },
        isFiltersActive() {
            return !isEmptyValues(this.filterQuery);
        },
        toolbarActions() {
            let actions = [];
            if (this.$can('show', 'Buttons/spaces/addTags')) {
                actions.push({
                    name: 'tags',
                    text: 'Tags',
                    icon: 'tag',
                    tooltip: 'Add/Remove tags'
                });
            }
            if (this.$can('show', 'Buttons/spaces/resetAccount')) {
                actions.push({
                    name: 'reset',
                    text: 'Reset Rooms',
                    icon: 'reset',
                    tooltip: 'Reset rooms account'
                });
            }
            if (this.$can('show', 'Buttons/spaces/checkInOut')) {
                actions.push({
                    name: 'checkin',
                    text: 'Check In',
                    icon: 'checkin'
                });
                actions.push({
                    name: 'checkout',
                    text: 'Check Out',
                    icon: 'checkout'
                });
            }
            if (this.$can('show', 'Buttons/spaces/assignChannelLineup')) {
                actions.push({
                    name: 'assignChannelLineup',
                    text: 'Assign Channel Lineup',
                    icon: 'tv'
                });
            }
            if (this.$can('show', 'Buttons/spaces/deleteSpaces')) {
                actions.push({
                    name: 'deleteByCriteria',
                    text: 'Delete',
                    icon: 'delete',
                    tooltip: 'Delete spaces',
                    color: 'red accent-4'
                });
            }
            return actions;
        },
        fullTopology() {
            if (this.allSpaces.length) {
                return topologyUtil.buildTopology(this.allSpaces);
            }
            return [];
        },
        topologyData() {
            if (this.filteredTopology) {
                return this.filteredTopology;
            }
            return this.fullTopology;
        }
    },
    async created() {
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
            await this.resetRequestQuery();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        setTimeout(() => {
            this.findSpaces(this.getInitialPageParams());
            this.getLastHealthPollDate({});
            this.fetchDeviceTags();
            this.fetchSpaceTags();
        }, 200);
    },
    methods: {
        // =================== Vuex Actions =====================
        ...mapActions('spaces', [
            'fetchSpaces',
            'setupView',
            'resetRequestQuery',
            'postBulkActions',
            'buildFilteredTopology',
            'fetchItems',
            'fetchAllSpaces'
        ]),
        ...mapActions('devices', {
            deviceBulkActions: 'postBulkActions',
            fetchLastHealthPollDate: 'fetchLastHealthPollDate'
        }),
        ...mapActions('tags', ['fetchDeviceTags', 'fetchSpaceTags']),
        // ===================== Events ========================
        onDataSearchBarChange(options) {
            const searchOptions = {
                ...options,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
            const isQueryChanged = searchOptions.query !== this.requestQuery.q;
            const isPageChanged =
                searchOptions.page !== this.requestQuery.number ||
                searchOptions.itemsPerPage !== this.requestQuery.size;
            this.findSpaces(searchOptions, this.filterQuery);
            if (isQueryChanged) {
                this.filterTopology(searchOptions, this.filterQuery);
            }
            if (isPageChanged) {
                this.tab = 0;
            }
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findSpaces(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        onAddSpaceClick(event) {
            this.spaceDetailsComponent = 'space-details-dialog';
            this.selectedSpaceId = '';
            this.showSpaceDetailsDialog = true;
            this.lastFocusedElement = event.target;
        },
        onViewSpaceClick(item, event) {
            this.spaceDetailsComponent = 'space-details-dialog';
            this.selectedSpaceId = item.id;
            this.showSpaceDetailsDialog = true;
            this.lastFocusedElement = event.target;
        },
        onCloseSpaceDetailsDialog(refreshData) {
            // if refreshData then we need to refresh the current data.
            this.spaceDetailsComponent = '';
            this.showSpaceDetailsDialog = false;
            this.selectedSpaceId = '';
            if (refreshData) {
                this.applyFilters(this.filterQuery, this.filterChips);
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onExportDataClick(event) {
            const searchParams = {
                number: 1,
                size:
                    this.dataSearchBarOptions.itemsLength <= 10000
                        ? this.dataSearchBarOptions.itemsLength
                        : 10000,
                q: this.dataSearchBarOptions.query,
                sort: this.sortBy,
                desc: this.sortDesc
            };
            const filterParams = this.filterQuery;
            this.exportParams = {
                searchParams,
                filterParams
            };
            this.exportDialogComponent = 'csv-data-exporter';
            this.lastFocusedElement = event.target;
        },
        onExportDialogClose() {
            this.exportDialogComponent = '';
            this.setNextFocus(this.lastFocusedElement);
        },
        onFilterClick() {
            this.showFilters = true;
        },
        onApplyFilters({ filters, filtersDesc }) {
            this.applyFilters(filters, filtersDesc);
        },
        onFilterChipsChange(filtersDesc) {
            if (filtersDesc) {
                let newFilters = {};
                filtersDesc.forEach(item => {
                    newFilters[item.name] = item.value;
                });
                this.applyFilters(newFilters, filtersDesc);
            }
        },
        async onToolbarClick({ item, selected }) {
            const action = item.name;
            const criteria = { ids: selected.map(item => item.id) };
            let needsRefresh = false;
            if (action === 'deleteByCriteria') {
                const res = await this.$refs.confirmationDialog.open(
                    'Delete Spaces',
                    'Are you sure you want to delete the selected spaces?',
                    '',
                    { showIcon: true }
                );
                if (res) {
                    await this.postBulkActions({ action, criteria });
                    needsRefresh = true;
                }
            } else if (action === 'tags') {
                // get all tags for the selected items
                let tagsFilter = [];
                selected.forEach(item => {
                    if (item.vmsTags && item.vmsTags.length) {
                        tagsFilter.push(...item.vmsTags.map(tag => tag.name));
                    }
                });
                const res = await this.$refs.tagsDialog.open(
                    'Add/Remove tags for selected spaces',
                    'space',
                    tagsFilter
                );
                if (res && res.added && res.added.length) {
                    await this.postBulkActions({
                        action: 'addTagsByCriteria',
                        criteria,
                        params: res.added
                    });
                    needsRefresh = true;
                }
                if (res && res.removed && res.removed.length) {
                    await this.postBulkActions({
                        action: 'removeTagsByCriteria',
                        criteria,
                        params: res.removed
                    });
                    needsRefresh = true;
                }
            } else if (action === 'assignChannelLineup') {
                let params = {};
                const res = await this.$refs.lineupSelection.open(
                    'Select a Channel Lineup'
                );
                if (res && res.id) {
                    params = { venueChannelMapId: res.id };
                    await this.postBulkActions({ action, criteria, params });
                    needsRefresh = true;
                } else {
                    return;
                }
            } else if (action === 'reset') {
                // need to collect device IDs for selected spaces.
                let deviceIds = [];
                selected.forEach(space => {
                    if (
                        space &&
                        space.serviceAccount &&
                        space.serviceAccount.devices
                    ) {
                        deviceIds.push(
                            ...space.serviceAccount.devices.map(
                                device => device.id
                            )
                        );
                    }
                });
                if (deviceIds && deviceIds.length) {
                    await this.deviceBulkActions({
                        action: 'resetDevice',
                        criteria: { ids: deviceIds }
                    });
                    needsRefresh = true;
                }
            } else {
                await this.postBulkActions({ action, criteria });
                needsRefresh = true;
            }
            if (needsRefresh) {
                // need to wait for DS search to refresh the data
                setTimeout(() => {
                    this.refreshView();
                }, 1500);
                this.selectedItems = [];
            }
        },
        onUploadClick() {
            this.showUploadDialog = true;
        },
        onUploadDialogClose(refreshParentData) {
            this.showUploadDialog = false;
            if (refreshParentData) {
                this.applyFilters(this.filterQuery, this.filterChips);
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onChartClick({ label }) {
            if (label === 'EMPTY') {
                this.applyHasDevicesFilter({ hasDevices: false });
            } else {
                this.applyStatusFilter({ healthCheckStatus: label });
            }
        },
        async onRefreshClick() {
            if (!this.filteredTopology) {
                await this.fetchAllSpaces({});
            }
            this.applyFilters(this.filterQuery, this.filterChips);
        },
        // ===================== Others ========================
        refreshView() {
            this.findSpaces(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
            this.getLastHealthPollDate();
        },
        applyFilters(filters, filtersDesc) {
            this.filterChips = filtersDesc;
            let filterTasks = [];
            // apply the filters to the list view first
            filterTasks.push(() =>
                this.findSpaces(
                    {
                        ...this.dataSearchBarOptions,
                        page: 1,
                        sortBy: this.sortBy,
                        sortDesc: this.sortDesc
                    },
                    filters
                )
            );
            // apply the filter to the topology view
            filterTasks.push(() =>
                this.filterTopology(this.dataSearchBarOptions, filters)
            );
            if (this.tab === 0) {
                filterTasks[0]();
                filterTasks[1]();
            } else {
                filterTasks[1]();
                filterTasks[0]();
            }
        },
        async filterTopology(searchOptions, filterOptions) {
            if (
                (searchOptions && searchOptions.query) ||
                (filterOptions && !isEmptyValues(filterOptions))
            ) {
                const notifyConfig = {
                    notifyRequestPending: false,
                    notifySucess: false
                };
                const searchParams = {
                    number: 1,
                    size: 9999,
                    q: searchOptions.query
                };
                const filterParams = { ...filterOptions };
                const res = await this.fetchItems({
                    searchParams,
                    filterParams,
                    notifyConfig
                });
                this.filteredTopology = topologyUtil.buildTopology(
                    res.data.content
                );
            } else {
                this.filteredTopology = null;
            }
        },
        async findSpaces(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    await this.fetchSpaces({ searchParams, filterParams });
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: this.requestQuery.q,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        },
        applyStatusFilter({ healthCheckStatus }) {
            const filterName = 'serviceAccount.devices.healthCheckStatus';
            if (healthCheckStatus !== this.filters[filterName]) {
                const newFilters = {
                    'serviceAccount.devices.healthCheckStatus': healthCheckStatus
                };
                const newFiltersDesc = [
                    {
                        name: filterName,
                        text: 'Health',
                        value: healthCheckStatus
                    }
                ];
                this.applyFilters(newFilters, newFiltersDesc);
            }
        },
        applyHasDevicesFilter({ hasDevices }) {
            if (hasDevices !== this.filters.hasDevices) {
                const newFilters = { hasDevices };
                const newFiltersDesc = [
                    {
                        name: 'hasDevices',
                        text: 'Has devices',
                        value: hasDevices
                    }
                ];
                this.applyFilters(newFilters, newFiltersDesc);
            }
        },
        getLastHealthPollDate() {
            this.fetchLastHealthPollDate({}).then(res => {
                if (res) {
                    this.lastHealthPollDate = new Date(res).toLocaleString();
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .scrollable-content.v-tabs-items {
    .v-window__container {
        height: 100%;
    }
    .v-window-item {
        height: 100%;
    }
}
::v-deep .scrollable-container {
    > .v-tabs {
        flex-grow: 0;
    }
}
::v-deep .v-tabs.thin-tabs {
    .v-tabs-bar {
        height: 35px;
    }
}
</style>
